import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Tooltip } from 'examkrackers-components'

import { QuizDiagnosticsChapter, QuizDiagnosticsTagColor } from '../types'
import {
  QUIZ_DIAGNOSTICS_DESKTOP_MAX_WIDTH,
  QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH
} from '../utils/quizDiagnosticsBreakpoints'
import {
  QUIZ_DIAGNOSTICS_PERCENT_COLOR,
  QUIZ_DIAGNOSTICS_PERCENT_LINE_HEIGHT,
  QUIZ_DIAGNOSTICS_PROGRESS_BAR_HEIGHT,
  QUIZ_DIAGNOSTICS_CHAPTER_BAR_WIDTH,
  QUIZ_DIAGNOSTICS_SECTION_MARGIN,
  QUIZ_DIAGNOSTICS_SUBTITLE_LINE_HEIGHT,
  QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH
} from '../utils/quizDiagnosticsBarLayout'
import { getQuizDiagnosticsPaletteColor } from '../utils/getQuizDiagnosticsPaletteColor'
import { useSrqStatus } from '../utils/srqStatusStore'

const SUBTITLE_CLAMP_LINES = 2

const getSubtitleTooltipId = (barTitle: string): string =>
  `quiz-diagnostics-subtitle-${barTitle.replace(/\s+/g, '-').toLowerCase()}`

type QuizDiagnosticsTruncatedSubtitleProps = {
  barTitle: string
  subtitle: string
}

const QuizDiagnosticsTruncatedSubtitle = ({
  barTitle,
  subtitle
}: QuizDiagnosticsTruncatedSubtitleProps): JSX.Element => {
  const subtitleRef = useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const updateTruncation = useCallback(() => {
    const element = subtitleRef.current

    if (!element) {
      return
    }

    setIsTruncated(element.scrollHeight > element.clientHeight + 1)
  }, [])

  useEffect(() => {
    updateTruncation()

    const element = subtitleRef.current

    if (!element) {
      return undefined
    }

    const resizeObserver = new ResizeObserver(updateTruncation)
    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [subtitle, updateTruncation])

  const subtitleContent = <Subtitle ref={subtitleRef}>{subtitle}</Subtitle>

  if (!isTruncated) {
    return subtitleContent
  }

  return (
    <Tooltip
      tooltipContent={
        <SubtitleTooltipContent>{subtitle}</SubtitleTooltipContent>
      }
      id={getSubtitleTooltipId(barTitle)}
      place='top'
    >
      {subtitleContent}
    </Tooltip>
  )
}

type QuizDiagnosticsProgressBarProps = {
  chapter?: QuizDiagnosticsChapter
  dueTodayCount?: number
  color: QuizDiagnosticsTagColor
  onBarClick?: () => void
  onTitleClick?: () => void
  percent: number
  title: string
  subtitle?: string | null
  titleAriaLabel?: string
  barWidth?: number
}

const QuizDiagnosticsProgressBar = ({
  chapter,
  dueTodayCount = 0,
  color,
  onBarClick,
  onTitleClick,
  percent,
  title,
  subtitle,
  titleAriaLabel,
  barWidth = QUIZ_DIAGNOSTICS_CHAPTER_BAR_WIDTH
}: QuizDiagnosticsProgressBarProps): JSX.Element => {
  const { t } = useTranslation()
  const { srqRedDotsVisible } = useSrqStatus()
  const isBarClickable = Boolean(chapter && onBarClick)
  const isTitleClickable = Boolean(chapter && onTitleClick)

  const handleBarClick = () => {
    if (!onBarClick) {
      return
    }

    onBarClick()
  }

  const handleDueTodayBadgeClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (!isBarClickable) {
      return
    }

    event.stopPropagation()
    handleBarClick()
  }

  const handleDueTodayBadgeKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (!isBarClickable) {
      return
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    handleBarClick()
  }

  const handleBarKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    handleBarClick()
  }

  const handleTitleClick = () => {
    if (!onTitleClick) {
      return
    }

    onTitleClick()
  }

  const handleTitleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    handleTitleClick()
  }

  const progressBar = (
    <ProgressBarFillMask>
      <ProgressBar $color={color} $percent={percent} />
    </ProgressBarFillMask>
  )
  const hasDueTodayBadge =
    srqRedDotsVisible &&
    (chapter?.srqRedDotsVisible ?? true) &&
    dueTodayCount > 0

  const barAriaLabel = chapter
    ? t('quizDiagnostics.chapterBar.openChapterDiagnostics', {
        chapter: title
      })
    : undefined

  const resolvedTitleAriaLabel =
    titleAriaLabel ??
    t('quizDiagnostics.chapterBar.openBookChapter', {
      chapter: title
    })

  const barButton = (
    <ProgressBarShell $barWidth={barWidth}>
      <ProgressBarButton
        type='button'
        aria-label={barAriaLabel}
        onClick={handleBarClick}
        onKeyDown={handleBarKeyDown}
        $color={color}
      >
        {progressBar}
      </ProgressBarButton>
      {hasDueTodayBadge ? (
        <DueTodayBadge
          aria-hidden
          $isClickable={isBarClickable}
          onClick={handleDueTodayBadgeClick}
          onKeyDown={handleDueTodayBadgeKeyDown}
          role={isBarClickable ? 'button' : undefined}
          tabIndex={isBarClickable ? 0 : undefined}
        >
          {dueTodayCount}
        </DueTodayBadge>
      ) : null}
    </ProgressBarShell>
  )

  const titleButton = (
    <TitleButton
      type='button'
      aria-label={resolvedTitleAriaLabel}
      onClick={handleTitleClick}
      onKeyDown={handleTitleKeyDown}
    >
      {title}
    </TitleButton>
  )

  const barContent = (
    <>
      <Percent>{`${percent}%`}</Percent>
      {isBarClickable ? (
        <Tooltip
          tooltipContent={t('quizDiagnostics.chapterBar.seeSectionDiagnostics')}
          id={`quiz-diagnostics-bar-${title
            .replace(/\s+/g, '-')
            .toLowerCase()}`}
          place='top'
        >
          {barButton}
        </Tooltip>
      ) : (
        <ProgressBarShell $barWidth={barWidth}>
          <ProgressBarContainer $color={color}>
            {progressBar}
          </ProgressBarContainer>
          {hasDueTodayBadge ? (
            <DueTodayBadge aria-hidden $isClickable={false}>
              {dueTodayCount}
            </DueTodayBadge>
          ) : null}
        </ProgressBarShell>
      )}
      <TitleContainer>
        {isTitleClickable ? (
          <Tooltip
            tooltipContent={t('quizDiagnostics.chapterBar.readChapter')}
            id={`quiz-diagnostics-title-${title
              .replace(/\s+/g, '-')
              .toLowerCase()}`}
            place='top'
          >
            {titleButton}
          </Tooltip>
        ) : (
          <Title $showUnderline={Boolean(chapter)}>{title}</Title>
        )}
      </TitleContainer>
    </>
  )

  return (
    <Container>
      <ContentColumn>
        {chapter ? (
          <BarTooltipTarget>{barContent}</BarTooltipTarget>
        ) : (
          barContent
        )}
        {subtitle ? (
          <QuizDiagnosticsTruncatedSubtitle
            barTitle={title}
            subtitle={subtitle}
          />
        ) : null}
      </ContentColumn>
    </Container>
  )
}

export default QuizDiagnosticsProgressBar

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  flex: 1;
  position: relative;
`

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH}px;
  flex: 1;
  margin-top: 8px;
`

const BarTooltipTarget = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const Percent = styled.div`
  font-family: Arial;
  font-size: 14px;
  font-style: bold;
  font-weight: 700;
  line-height: ${QUIZ_DIAGNOSTICS_PERCENT_LINE_HEIGHT}px;
  color: ${QUIZ_DIAGNOSTICS_PERCENT_COLOR};
  margin-bottom: ${QUIZ_DIAGNOSTICS_SECTION_MARGIN - 1}px;
`

const ProgressBarShell = styled.div<{ $barWidth: number }>`
  position: relative;
  width: ${({ $barWidth }) => $barWidth}px;
  height: ${QUIZ_DIAGNOSTICS_PROGRESS_BAR_HEIGHT}px;
  margin-bottom: ${QUIZ_DIAGNOSTICS_SECTION_MARGIN}px;
  overflow: visible;
`

const ProgressBarContainer = styled.div<{
  $color: QuizDiagnosticsTagColor
}>`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.main.white};
  border: 0.68px solid
    ${({ theme, $color }) =>
      getQuizDiagnosticsPaletteColor($color, 'stroke') ??
      theme.colors.tags[$color]?.backgroundActive};
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  box-shadow: 0px 1px 4px 0px #00000040;
`

const ProgressBarButton = styled.button<{
  $color: QuizDiagnosticsTagColor
}>`
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: ${({ theme }) => theme.colors.main.white};
  border: 0.68px solid
    ${({ theme, $color }) =>
      getQuizDiagnosticsPaletteColor($color, 'stroke') ??
      theme.colors.tags[$color]?.backgroundActive};
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  box-shadow: 0px 1px 4px 0px #00000040;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #f7961b;
    outline-offset: 2px;
  }
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH}px;
  text-align: center;
`

const Title = styled.div<{ $showUnderline: boolean }>`
  font-family: Arial;
  font-size: 14px;
  font-weight: 700;
  line-height: 14px;
  min-width: 0;
  width: 100%;
  margin-bottom: 2px;
  text-decoration: ${({ $showUnderline }) =>
    $showUnderline ? 'underline' : 'none'};

  @media (max-width: ${QUIZ_DIAGNOSTICS_DESKTOP_MAX_WIDTH}px) {
    font-size: 13px;
  }

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    min-width: 72px;
  }
  color: #444444;
`

const TitleButton = styled.button`
  font-family: Arial;
  font-size: 14px;
  font-weight: 700;
  line-height: 14px;
  min-width: 0;
  width: 100%;
  margin: 0 0 2px;
  padding: 0;
  border: none;
  background: transparent;
  text-decoration: underline;
  color: #444444;
  cursor: pointer;
  text-align: center;

  &:focus-visible {
    outline: 2px solid #f7961b;
    outline-offset: 2px;
  }

  @media (max-width: ${QUIZ_DIAGNOSTICS_DESKTOP_MAX_WIDTH}px) {
    font-size: 13px;
  }

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    min-width: 72px;
  }
`

const Subtitle = styled.div`
  font-size: 10px;
  font-weight: 400;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  line-height: 11.5px;
  min-height: calc(
    ${QUIZ_DIAGNOSTICS_SUBTITLE_LINE_HEIGHT}px * ${SUBTITLE_CLAMP_LINES}
  );
  max-height: calc(
    ${QUIZ_DIAGNOSTICS_SUBTITLE_LINE_HEIGHT}px * ${SUBTITLE_CLAMP_LINES}
  );
  display: -webkit-box;
  -webkit-line-clamp: ${SUBTITLE_CLAMP_LINES};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  cursor: default;
  text-align: center;
  font-family: Arial;
  color: #444444;
`

const SubtitleTooltipContent = styled.div`
  padding: 5px;
  max-width: 294px;
  font-size: 10px;
  font-weight: 400;
  line-height: ${QUIZ_DIAGNOSTICS_SUBTITLE_LINE_HEIGHT}px;
  text-align: center;
  white-space: normal;
  word-break: break-word;
`

const ProgressBar = styled.div<{
  $color: QuizDiagnosticsTagColor
  $percent: number
}>`
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  background: ${({ theme, $color }) =>
    getQuizDiagnosticsPaletteColor($color, 'chapter') ??
    theme.colors.tags[$color]?.backgroundActive};
  border-radius: 2px 2px 0 0;
  transition: height 0.3s ease-in-out;
  height: ${({ $percent }) => `calc(${$percent} * 1%)`};
`

const ProgressBarFillMask = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 0;
  position: relative;
  overflow: hidden;
`

const DueTodayBadge = styled.div<{ $isClickable: boolean }>`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ea4335;
  color: #ffffff;
  font-size: 8px;
  font-weight: 700;
  line-height: 12px;
  text-align: center;
  z-index: 2;
  pointer-events: auto;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};

  &:focus-visible {
    outline: 2px solid #f7961b;
    outline-offset: 2px;
  }
`
